/**
 * Chapter reader — focused full-screen view of a single chapter or appendix.
 *
 * Receives Markdown content from the parent viewer (already loaded) and
 * renders it through the dark-theme Markdown components. Restores scroll
 * position on item change and provides a back button to return to the
 * manuscript index.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React, { useEffect, useRef } from "react";
import ReactMarkdown from "react-markdown";
import { REHYPE_PLUGINS, REMARK_PLUGINS } from "./markdown-renderer.js";
import type {
  Manuscript,
  ManuscriptAppendix,
  ManuscriptChapter,
  ManuscriptItemKind,
  ManuscriptViewerTheme,
} from "./types.js";

interface ChapterReaderProps {
  readonly manuscript: Manuscript;
  readonly item: ManuscriptChapter | ManuscriptAppendix;
  readonly kind: ManuscriptItemKind;
  readonly content: string | null;
  readonly loading: boolean;
  readonly onClose: () => void;
  readonly theme: ManuscriptViewerTheme;
  readonly unavailableMessage: string;
  readonly markdownComponents: React.ComponentProps<typeof ReactMarkdown>["components"];
}

export function ChapterReader({
  manuscript,
  item,
  kind,
  content,
  loading,
  onClose,
  theme,
  unavailableMessage,
  markdownComponents,
}: ChapterReaderProps): React.JSX.Element {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (scrollRef.current !== null) scrollRef.current.scrollTop = 0;
  }, [item.id, manuscript.id]);

  const partKey = "part" in item ? item.part : undefined;
  const partColor =
    partKey !== undefined && manuscript.parts !== undefined
      ? (manuscript.parts[partKey]?.color ?? theme.muted)
      : theme.muted;

  const label = kind === "chapter" ? `§${item.id}` : `App ${item.id}`;
  const showUnavailable = !loading && (content === null || content.trim().length === 0);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        background: theme.void,
      }}
    >
      <div
        style={{
          padding: "16px 24px",
          borderBottom: `1px solid ${theme.border}`,
          background: theme.surface,
          display: "flex",
          alignItems: "center",
          gap: 12,
          flexShrink: 0,
        }}
      >
        <button
          type="button"
          onClick={onClose}
          style={{
            background: "none",
            border: `1px solid ${theme.border}`,
            borderRadius: 8,
            color: theme.text,
            cursor: "pointer",
            fontFamily: theme.serif,
            fontSize: 12,
            fontWeight: 600,
            letterSpacing: 0,
            padding: "5px 11px",
          }}
        >
          ← Back
        </button>
        <span
          title={label}
          style={{
            fontFamily: theme.serif,
            fontSize: 13,
            color: partColor,
            fontWeight: 700,
            letterSpacing: 0,
            maxWidth: "min(28vw, 260px)",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {label}
        </span>
        <span
          style={{
            color: theme.bright,
            flex: 1,
            fontFamily: theme.serif,
            fontSize: 16,
            fontWeight: 520,
            lineHeight: 1.3,
          }}
        >
          {item.title}
        </span>
        {item.words !== undefined ? (
          <span
            style={{
              color: theme.muted,
              fontFamily: theme.serif,
              fontSize: 12,
              whiteSpace: "nowrap",
            }}
          >
            {item.words.toLocaleString()} words
          </span>
        ) : null}
      </div>
      <div
        ref={scrollRef}
        style={{ flex: 1, overflow: "auto", padding: "20px 32px 60px", fontFamily: theme.serif }}
      >
        {loading ? (
          <p style={{ color: theme.muted, fontStyle: "italic" }}>Loading…</p>
        ) : showUnavailable ? (
          <p style={{ color: theme.muted, fontStyle: "italic" }}>{unavailableMessage}</p>
        ) : (
          <div style={{ maxWidth: 720 }}>
            <ReactMarkdown
              remarkPlugins={REMARK_PLUGINS}
              rehypePlugins={REHYPE_PLUGINS}
              components={markdownComponents}
            >
              {content ?? ""}
            </ReactMarkdown>
          </div>
        )}
      </div>
    </div>
  );
}
